import { EntityType, Collateral, Organisation, CollateralBasket, Side, PlatformFeature, MarketType } from 'api'
import { isEmpty } from 'lodash'
import { hasTradingRelationship } from 'model'
import { createSelector } from '@reduxjs/toolkit'
import { createAllEntitiesSelector } from 'selectors/entitySelectors'
import {
  createTradingEntityCanTradeCollateralBasketsSelector,
  createTradingEntityCanTradeCollaterals
} from 'selectors/tradingEntitySelectors'

export const createOrganisationCanTradeCollateralsSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY)],
  tradingEntities => <T extends { collateral: Collateral; marketType: MarketType | null }>(details: T[]) => {
    const tradingEntityCanTradeCollaterals = createTradingEntityCanTradeCollaterals(details)

    return (organisation: Organisation) => {
      const organisationTradingEntities = tradingEntities.filter(te => te.organisationId === organisation.id)
      return organisationTradingEntities.length > 0 && organisationTradingEntities.some(te => tradingEntityCanTradeCollaterals(te))
    }
  }
)

export const createOrganisationCanTradeCollateralBasketsSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY), createTradingEntityCanTradeCollateralBasketsSelector],
  (tradingEntities, createTradingEntityCanTradeCollateralBaskets) => <
    T extends { collateralBasket: CollateralBasket; marketType: MarketType | null }
  >(
    details: T[]
  ) => {
    const tradingEntityCanTradeCollateralBaskets = createTradingEntityCanTradeCollateralBaskets(details)
    return (organisation: Organisation) => {
      const organisationTradingEntities = tradingEntities.filter(te => te.organisationId === organisation.id)
      return organisationTradingEntities.length > 0 && organisationTradingEntities.some(te => tradingEntityCanTradeCollateralBaskets(te))
    }
  }
)

export const createOrganisationHasTradingRelationshipWithFundsAsSideSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP), createAllEntitiesSelector(EntityType.TRADING_ENTITY)],
  (tradingRelationships, tradingEntities) => (fundIds: string[], side: Side) => (organisation: Organisation) => {
    const organisationTradingEntities = tradingEntities.filter(te => te.organisationId === organisation.id)

    return isEmpty(fundIds)
      ? true
      : organisationTradingEntities.some(organistionTradingEntity =>
          fundIds.every(fundId =>
            hasTradingRelationship(
              side === Side.BUY ? fundId : organistionTradingEntity.id,
              side === Side.SELL ? fundId : organistionTradingEntity.id,
              tradingRelationships,
              false,
              []
            )
          )
        )
  }
)

export const organisationHasRepoTypeSBL = (organisation: Organisation) =>
  organisation.enabledFeatures.includes(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING)
